import { ArrowDown, ArrowUp, Lock, Trash } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "./shared";

export const isSystemId = (id) => ["name", "work_date", "location_count"].includes(id) || /^job_\d+$/.test(id);
export const SYSTEM = { includes: isSystemId };
const KIND = { employee: "Employee list", date: "Date", choice: "Multiple choice", jobsite: "Jobsite list", text: "Short text", number: "Number" };
const OPS = [["eq", "is"], ["neq", "is not"], ["gte", "≥"], ["lte", "≤"]];
const sel = "h-8 rounded-md border border-slate-200 bg-white px-2 text-xs";

function Condition({ q, earlier, onChange }) {
  const c = q.show_if || {};
  if (SYSTEM.includes(q.id)) {
    return c.q ? <p className="text-[11px] text-slate-500">Shown when <b>locations worked</b> ≥ {c.value} (matches the original Typeform branching)</p> : null;
  }
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
      Show
      <select className={sel} value={c.q || ""} onChange={(e) => onChange(e.target.value ? { q: e.target.value, op: c.op || "eq", value: c.value || "" } : null)} data-testid={`qe-cond-q-${q.id}`}>
        <option value="">always</option>{earlier.map((x) => <option key={x.id} value={x.id}>when “{x.title.slice(0, 32)}”</option>)}
      </select>
      {c.q && <>
        <select className={sel} value={c.op} onChange={(e) => onChange({ ...c, op: e.target.value })} data-testid={`qe-cond-op-${q.id}`}>{OPS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <Input className="h-8 w-28 text-xs" value={c.value} onChange={(e) => onChange({ ...c, value: e.target.value })} placeholder="value" data-testid={`qe-cond-value-${q.id}`} />
      </>}
    </div>
  );
}

export default function QuestionEditor({ q, i, all, onChange, onMove, onRemove }) {
  const sys = SYSTEM.includes(q.id);
  const set = (k) => (v) => onChange({ ...q, [k]: v });
  const canUp = i > 0 && !(sys && SYSTEM.includes(all[i - 1].id));
  const canDown = i < all.length - 1 && !(sys && SYSTEM.includes(all[i + 1].id));
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2.5" data-testid={`qe-${q.id}`}>
      <div className="flex items-center gap-2">
        <span className="inline-flex h-6 min-w-[24px] items-center justify-center rounded-md bg-[#327de3] text-white text-xs font-bold">{i + 1}</span>
        <Badge tone={sys ? "slate" : "blue"}>{KIND[q.kind]}</Badge>
        {sys && <span className="inline-flex items-center gap-1 text-[11px] text-slate-400"><Lock size={11} />Feeds the dashboard — can't be removed</span>}
        <div className="ml-auto flex items-center">
          <button type="button" disabled={!canUp} onClick={() => onMove(i, -1)} className="p-1 text-slate-400 hover:text-slate-800 disabled:opacity-25" data-testid={`qe-up-${q.id}`}><ArrowUp size={14} /></button>
          <button type="button" disabled={!canDown} onClick={() => onMove(i, 1)} className="p-1 text-slate-400 hover:text-slate-800 disabled:opacity-25" data-testid={`qe-down-${q.id}`}><ArrowDown size={14} /></button>
          {!sys && <button type="button" onClick={() => onRemove(i)} className="p-1 text-rose-400 hover:text-rose-600" data-testid={`qe-remove-${q.id}`}><Trash size={14} /></button>}
        </div>
      </div>
      <Input value={q.title} onChange={(e) => set("title")(e.target.value)} placeholder="Question" className="font-semibold" data-testid={`qe-title-${q.id}`} />
      <Textarea value={q.helper || ""} onChange={(e) => set("helper")(e.target.value)} placeholder="Helper / translation text (optional)" rows={2} className="text-sm" data-testid={`qe-helper-${q.id}`} />
      {q.kind === "choice" && !sys && (
        <Textarea value={(q.options || []).join("\n")} onChange={(e) => set("options")(e.target.value.split("\n"))} rows={3} placeholder="One option per line" className="text-sm" data-testid={`qe-options-${q.id}`} />
      )}
      {q.id === "location_count" && <p className="text-[11px] text-slate-500">Options follow “Maximum jobs/locations” above so each answer maps to a jobsite step.</p>}
      {q.kind === "jobsite" && <p className="text-[11px] text-slate-500">Choices come from the Jobs / Properties tab.</p>}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Condition q={q} earlier={all.slice(0, i)} onChange={set("show_if")} />
        {!sys && <label className="flex items-center gap-2 text-xs text-slate-600"><Switch checked={!!q.required} onCheckedChange={set("required")} data-testid={`qe-required-${q.id}`} />Required</label>}
      </div>
    </div>
  );
}
